import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { atom, useAtom, useAtomValue } from "jotai";

import {
  ReusableViewIDs,
  SubViewTypes,
  View,
  ViewTypes,
} from "../../types/PageInterfaces";
import PageViewDocumentMultiple from "./PageViewDocument/PageViewDocumentMultiple";
import PageViewFlowRenderer from "./PageViewFlowChart/PageViewFlowRenderer";
import { acceptableModes, currentViewAtom } from "../../atoms/ViewAtoms";
import PageViewDashboard from "./PageViewDashboard/PageViewDashboard";
import { ViewTypesWithModule } from "./AddViewDrawer/AddViewDrawer";
import PageViewDocument from "./PageViewDocument/PageViewDocument";
import PageViewBrieGPT from "./PageViewBrieGPT/PageViewBrieGPT";
import PageViewShared from "./PageViewTable/PageViewShared";
import AppEmptyState from "../../elements/AppEmptyState";
import { Page } from "../../types/GlobalInterfaces";
import AppHelmet from "../AppComponents/AppHelmet";
import { IsMobileAtom } from "../../App";

export type SharedViewModes =
  | "Table"
  | "Cards"
  | "Board"
  | "Timeline"
  | "Calendar";

const PageViewRenderer = ({
  page,
  pageLoading,
  view,
  viewLoading,
  viewID,
  parentViewID,
  record,
  showInsights,
  setAddViewDrawer,
  commentsWidget,
  reusableViews,
}: {
  page: Page;
  pageLoading: boolean;
  view: View | null;
  viewLoading: boolean;
  viewID: string;
  parentViewID?: string;
  record?: any;
  showInsights?: boolean;
  setAddViewDrawer?: Dispatch<SetStateAction<boolean>>;
  commentsWidget?: boolean;
  reusableViews?: ReusableViewIDs;
}) => {
  const [viewMode, setViewMode] = useViewMode({ view, viewID, viewLoading });
  const toggleModal = useRef<any>();
  toggleModal.current = () => {
    setAddViewDrawer && setAddViewDrawer(true);
  };

  const renderView = useMemo(() => {
    const visibleViews = page?.Views.filter((view) => !view.Hide);
    const isReusableView = page?.Views?.some(
      (pv) => !!pv.ReusableView && pv.id === viewID
    );

    /**
     * This section will only be displayed when:
     * 1. The view is not the origianl (reused view).
     * 2. The original view was deleted.
     */
    if (!view && isReusableView) {
      return (
        <div className="tw:flex tw:flex-col tw:justify-center tw:items-center tw:pt-10">
          <div className="tw:pb-3">
            <i
              className="mdi mdi-table-off tw:text-primary"
              style={{ fontSize: 100, color: "primary" }}
            />
          </div>
          <div className="">
            <h1 className="tw:text-dark tw:text-3xl">
              Reusable View Not Found
            </h1>
            <div className="tw:flex tw:justify-center">
              <p className="tw:text-dark tw:pt-4">
                The view reused in this page no longer exists.
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (pageLoading || viewLoading) {
      return (
        <>
          <div
            className="skeleton tw:mb-3"
            style={{ width: "100%", height: "32px" }}
          ></div>
          <div
            className="skeleton"
            style={{ width: "100%", height: "300px" }}
          ></div>
        </>
      );
    }

    if (
      !viewLoading &&
      !pageLoading &&
      (!page?.Views || page.Views?.length === 0)
    )
      return (
        <AppEmptyState
          title="Create your first View"
          description={`This page doesn't have any Views added yet. Click "Create View" to create your first view.`}
          buttonTitle="Create View"
          onClick={toggleModal.current}
          icon="mdi-view-array-outline"
          useEnterKey={true}
        />
      );

    if (!visibleViews?.length) {
      return (
        <AppEmptyState
          title="Views Hidden"
          description={`This page currently has all of it's Views hidden. Go to the Page settings to unhide a view, or create a new View.`}
          icon="mdi-view-array-outline"
          onClick={toggleModal.current}
          buttonTitle="Create View"
        />
      );
    }

    const type = view?.Type;

    if (!type) return null;

    // Table, Cards, Board - Are shared views
    if (ViewTypesWithModule.includes(type)) {
      return (
        <PageViewShared
          key={viewID}
          view={view}
          parentViewID={parentViewID}
          viewMode={viewMode || view?.Type}
          setViewMode={setViewMode}
          record={record}
          showInsights={showInsights}
          reusableViews={reusableViews}
          commentsWidget={commentsWidget}
        />
      );
    }

    let viewType: ViewTypes | SubViewTypes | "Document-Multiple" = view?.Type;

    if (viewType === "Document") {
      viewType =
        view?.DocumentPageType === "Multiple"
          ? "Document-Multiple"
          : "Document";
    }

    switch (viewType) {
      case "FlowChart":
        return (
          <PageViewFlowRenderer
            key={viewID}
            view={view}
            page={page}
            viewLoading={viewLoading}
          />
        );
      case "Dashboard":
        return (
          <PageViewDashboard
            key={viewID}
            page={page}
            view={view}
            parentViewID={parentViewID}
            record={record}
          />
        );
      case "Document":
        return (
          <PageViewDocument
            key={viewID}
            view={view}
            page={page}
            // @ts-ignore
            document={view?.Record}
          />
        );
      case "Document-Multiple":
        return (
          <PageViewDocumentMultiple
            key={viewID}
            view={view}
            page={page}
            viewLoading={viewLoading}
          />
        );
      case "BrieGPT":
        return (
          <PageViewBrieGPT
            key={viewID}
            view={view}
            page={page}
            viewLoading={viewLoading}
          />
        );
      default:
        return "None.";
    }
  }, [view, showInsights, viewMode, viewLoading, page?.Views?.length]);

  return (
    <>
      <AppHelmet title={pageLoading ? "Loading page..." : page?.Name} />
      <div className="px-3 px-sm-4 pb-4 pt-2">{renderView}</div>
    </>
  );
};

// Keeps track of what a views last viewMode was
export const viewModeHistory = atom<{
  [viewID: string]: SharedViewModes;
}>({});

function useViewMode({
  view,
  viewID,
  viewLoading,
}: {
  view: View | null;
  viewID: string;
  viewLoading: boolean;
}) {
  const isMobile = useAtomValue(IsMobileAtom);
  const currentView = useAtomValue(currentViewAtom);
  const [history, setHistory] = useAtom(viewModeHistory);

  const [viewMode, setViewMode] = useState<SharedViewModes>(() => {
    const viewType =
      isMobile && (currentView?.MobileType || view?.MobileType)
        ? currentView?.MobileType || view?.MobileType
        : currentView?.Type || view?.Type;

    return GetInitialMode(
      viewID,
      acceptableModes.includes(viewType || "") ? viewType : "Table"
    );
  });

  useEffect(() => {
    if (viewLoading || !view?.Type) return;

    const useMobileViewType = isMobile && view.MobileType;
    const viewType = useMobileViewType ? view.MobileType : view.Type;

    setViewMode(GetInitialMode(viewID, viewType));
  }, [viewLoading, viewID, isMobile, view?.Type, view?.MobileType]);

  function GetInitialMode(vID: string, viewType?: string) {
    if (!vID && viewType) return viewType as SharedViewModes;
    if (!vID && !viewType) return "Table";

    const viewMode = history?.[vID];

    return (viewMode || viewType || "Table") as SharedViewModes;
  }

  function setViewModeWithHistory(newMode: SharedViewModes) {
    setHistory((prev) => ({ ...prev, [viewID]: newMode }));
    setViewMode(newMode);
  }

  return [viewMode, setViewModeWithHistory] as const;
}

export default PageViewRenderer;
